import { log } from '@/utils/core/vueLogger.js';
import { eventBus } from '@/utils/core/eventBus.js';

/**
 * Memory-pressure seam.
 *
 * This repo is the web layer only; the iOS WKWebView Swift wrapper lives in a separate
 * project and there is currently NO native↔JS bridge. This module is the JS-side
 * ingress that the native shell can call to make the web app shed RAM *before* the
 * iOS jetsam killer terminates the WebContent process.
 *
 * Native contract (to be implemented in the Swift wrapper — not in this repo):
 *   1. didReceiveMemoryWarning →
 *        webView.evaluateJavaScript("window.__ecosMemoryWarning && window.__ecosMemoryWarning('native')")
 *      (or postMessage({ type: 'MEMORY_WARNING' }) to the web view).
 *   2. Optionally, periodically push the process's remaining memory so JS can purge
 *      proactively:  window.__ecosAvailableMemory = <megabytes remaining>
 *      (backed by os_proc_available_memory() on the native side).
 *   3. webViewWebContentProcessDidTerminate → reload the web view cleanly (Swift-only,
 *      cannot be done from JS; documented here for completeness).
 */

const handlers = [];
let initialized = false;
let lastFireTs = 0;
const MIN_INTERVAL_MS = 2000; // throttle warning bursts

export function registerMemoryPressureHandler(fn) {
	if (typeof fn === 'function' && !handlers.includes(fn)) handlers.push(fn);
}

/**
 * Best-effort remaining-memory reading in MB, or null if unknown.
 * Prefers a native-provided value; falls back to web heuristics (which are coarse
 * and, on Safari, usually unavailable).
 */
export function getAvailableMemoryMB() {
	if (typeof window.__ecosAvailableMemory === 'number' && isFinite(window.__ecosAvailableMemory)) {
		return window.__ecosAvailableMemory;
	}
	const mem = performance.memory;
	if (mem && isFinite(mem.jsHeapSizeLimit) && isFinite(mem.usedJSHeapSize)) {
		return Math.max(0, (mem.jsHeapSizeLimit - mem.usedJSHeapSize) / (1024 * 1024));
	}
	return null;
}

export function triggerMemoryPressure(source = 'unknown') {
	const now = Date.now();
	if (now - lastFireTs < MIN_INTERVAL_MS) return;
	lastFireTs = now;

	log('warn', 'MemoryPressure', `⚠️ Memory pressure (${source}) — flushing caches`);

	handlers.forEach((fn) => {
		try {
			fn(source);
		} catch (error) {
			log('error', 'MemoryPressure', `Handler failed: ${error.message}`);
		}
	});

	// Let any mounted component react (e.g. tear down a heavy view) without coupling.
	eventBus.emit('memory-pressure', { source });
}

/**
 * Install the JS-side ingress and register flush handlers. Idempotent.
 * @param {Function[]} flushHandlers - functions to call on pressure (e.g. cache flushers).
 */
export function initMemoryPressure(flushHandlers = []) {
	flushHandlers.forEach(registerMemoryPressureHandler);

	if (initialized) return;
	initialized = true;

	// Ingress A: native evaluateJavaScript hook.
	window.__ecosMemoryWarning = (source = 'native') => triggerMemoryPressure(source);

	// Ingress B: postMessage from the native shell (or any same-window emitter).
	window.addEventListener('message', (event) => {
		if (event.origin !== window.location.origin) return;
		if (event.data && event.data.type === 'MEMORY_WARNING') {
			triggerMemoryPressure(event.data.source || 'postMessage');
		}
	});

	log('info', 'MemoryPressure', '✅ Memory-pressure seam installed');
}
